import React, { useEffect, useState } from "react";
import { api } from "../api/client";
import { roleLabel } from "../lib/roleLabels";
import { Pencil, Trash2, X, AlertCircle, UserPlus, ShieldAlert, Key, Shield, ShieldOff, Copy, Check } from "lucide-react";

const ROLES = ["admin", "sales", "collections", "location", "clinic"];
// Partner view (2026-10): an outside referral partner's login. Create-only
// (a stopgap until X3b's invite flow); it needs the company's tenant id.
// Outside accounts keep their role and email; edit can only deactivate,
// reactivate or set a password (which forces a change at next sign-in).
const CREATE_ROLES = [...ROLES, "partner"];
const OUTSIDE_ROLES = ["partner", "client", "firm_user", "firm_admin"];
const isOutsideAccount = (u) => !!u && (OUTSIDE_ROLES.includes(String(u.role || "").toLowerCase()) || u.user_type === "external" || !!u.tenant_id);
// Roles whose access is tethered to a location SET (`user_locations`):
// clinic = network-clinic slugs, location (WS Location) = Raintree codes.
const LOCATION_SET_ROLES = ["clinic", "location"];
const ROLE_BADGE = {
  admin: "bg-purple-100 text-purple-700",
  sales: "bg-blue-100 text-blue-700",
  collections: "bg-emerald-100 text-emerald-700",
  location: "bg-amber-100 text-amber-700",
  clinic: "bg-teal-100 text-teal-700",
  partner: "bg-rose-100 text-rose-700",
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 border border-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const EMPTY_FORM = { username: "", email: "", password: "", role: "sales", is_active: true, force_password_change: true, location_code: "", location_codes: [], tenant_id: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "create" | "edit" | "delete" | "reset-password"
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null); // User to delete
  const [resetUser, setResetUser] = useState(null); // User for password reset
  const [tempPassword, setTempPassword] = useState(null); // Temporary password to display
  const [copied, setCopied] = useState(false); // Track if password was copied
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Reset-MFA confirm dialog state (separate from password-reset so
  // the two flows don't share UI state and accidentally cross-pollinate
  // the success messages).
  const [mfaResetUser, setMfaResetUser] = useState(null);
  const [mfaResetting, setMfaResetting] = useState(false);
  const [mfaResetResult, setMfaResetResult] = useState(null);  // "ok" | null
  const [error, setError] = useState("");

  const fetchUsers = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setError(""); setModal("create"); };
  const openEdit = (u) => { setEditUser(u); setForm({ username: u.username, email: u.email, password: "", role: u.role, is_active: u.is_active, force_password_change: u.must_change_password, location_code: u.location_code || "", location_codes: u.location_codes || (u.location_code ? [u.location_code] : []) }); setError(""); setModal("edit"); };
  const openDeleteConfirm = (u) => { setDeleteUser(u); setModal("delete"); };
  const openResetPassword = (u) => { setResetUser(u); setTempPassword(null); setCopied(false); setError(""); setModal("reset-password"); };
  const openResetMfa = (u) => { setMfaResetUser(u); setMfaResetResult(null); setError(""); setModal("reset-mfa"); };
  const closeModal = () => { setModal(null); setEditUser(null); setDeleteUser(null); setResetUser(null); setTempPassword(null); setCopied(false); setMfaResetUser(null); setMfaResetResult(null); setError(""); };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const payload = { ...form };
      // Only send locations for CLINIC users; backend requires them then,
      // ignores them otherwise. Multi-location logins (2026-08): the set is
      // authoritative; legacy scalar = first code for compatibility.
      if (!LOCATION_SET_ROLES.includes(payload.role)) { payload.location_code = null; payload.location_codes = null; }
      else { payload.location_code = payload.location_codes[0] || null; }
      if (payload.role === "partner") { payload.tenant_id = (payload.tenant_id || "").trim(); payload.force_password_change = true; }
      else { delete payload.tenant_id; }
      await api.post("/admin/users", payload);
      fetchUsers(); closeModal();
    } catch (err) {
      setError(err.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    const outside = isOutsideAccount(editUser);
    const payload = outside
      ? { is_active: form.is_active, force_password_change: form.force_password_change }
      : { role: form.role, email: form.email, is_active: form.is_active, force_password_change: form.force_password_change };
    if (form.password) payload.password = form.password;
    if (!outside && LOCATION_SET_ROLES.includes(form.role)) { payload.location_codes = form.location_codes; payload.location_code = form.location_codes[0] || null; }
    try {
      await api.put(`/admin/users/${editUser.id}`, payload);
      fetchUsers(); closeModal();
    } catch (err) {
      setError(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const handleForceReset = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { force_password_change: true });
      fetchUsers();
    } catch (err) {
      alert("Update failed");
    }
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    setResetting(true);
    setError("");
    try {
      const response = await api.post(`/admin/users/${resetUser.id}/reset-password`);
      setTempPassword(response.data.temporary_password);
      fetchUsers(); // Refresh to show must_change_password badge
    } catch (err) {
      setError(err.response?.data?.detail || "Password reset failed");
    } finally {
      setResetting(false);
    }
  };

  const handleResetMfa = async () => {
    if (!mfaResetUser) return;
    setMfaResetting(true);
    setError("");
    try {
      await api.post(`/admin/users/${mfaResetUser.id}/reset-mfa`);
      setMfaResetResult("ok");
      fetchUsers();  // refresh in case the list ever surfaces mfa state
    } catch (err) {
      setError(err.response?.data?.detail || "MFA reset failed");
    } finally {
      setMfaResetting(false);
    }
  };

  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      fetchUsers();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      fetchUsers();
    } catch (err) {
      alert("Update failed");
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-8 backdrop-blur-md">
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>User Management</h1>
        <button
          data-testid="create-user-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <UserPlus size={15} /> Add User
        </button>
      </div>

      <div className="p-8 space-y-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <p className="text-sm text-slate-500">{users.length} users registered</p>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    {["Username", "Email", "Role", "Status", "Pwd Reset", "Created", "Actions"].map((h) => (
                      <th key={h} className="p-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="p-4 font-medium text-slate-900">{u.username || "-"}</td>
                      <td className="p-4 text-slate-500">{u.email}</td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-600'}`}>{roleLabel(u.role)}</span>
                      </td>
                      <td className="p-4">
                        <button
                          data-testid={`toggle-active-${u.id}`}
                          onClick={() => toggleActive(u)}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${u.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {u.must_change_password && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <ShieldAlert size={11} />Pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-slate-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`edit-user-${u.id}`}
                            onClick={() => openEdit(u)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            data-testid={`reset-password-${u.id}`}
                            onClick={() => openResetPassword(u)}
                            className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Reset password"
                          >
                            <Key size={14} />
                          </button>
                          <button
                            data-testid={`reset-mfa-${u.id}`}
                            onClick={() => openResetMfa(u)}
                            className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                            title="Reset MFA"
                          >
                            <ShieldOff size={14} />
                          </button>
                          {/* Outside accounts are deactivate-only (2026-10). */}
                          {!isOutsideAccount(u) && (
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={() => openDeleteConfirm(u)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete user"
                          >
                            <Trash2 size={14} />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {modal === "create" && (
        <Modal title="Add New User" onClose={closeModal}>
          {error && (
            <div data-testid="user-form-error" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
              <AlertCircle size={15} />{error}
            </div>
          )}
          <form onSubmit={handleCreate} className="space-y-4">
            <FormFields form={form} setForm={setForm} isCreate />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
              <button data-testid="submit-create-user" type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {saving ? "Creating…" : "Create User"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === "edit" && (
        <Modal title={`Edit — ${editUser?.username || editUser?.email}`} onClose={closeModal}>
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
              <AlertCircle size={15} />{error}
            </div>
          )}
          <form onSubmit={handleEdit} className="space-y-4">
            <FormFields form={form} setForm={setForm} isCreate={false} outside={isOutsideAccount(editUser)} />
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
              {isOutsideAccount(editUser) ? (
                <span className="text-xs text-slate-500" data-testid="outside-no-delete">Outside accounts can be deactivated, not deleted.</span>
              ) : (
              <button 
                type="button" 
                onClick={() => openDeleteConfirm(editUser)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                data-testid="delete-user-btn"
              >
                <Trash2 size={16} />
                Delete User
              </button>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                <button data-testid="submit-edit-user" type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {modal === "delete" && deleteUser && (
        <Modal title="Delete User" onClose={closeModal}>
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 size={24} className="text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Delete "{deleteUser.username || deleteUser.email}"?
            </h3>
            <p className="text-slate-500 text-sm mb-6">
              This action cannot be undone. The user will be permanently removed from the system.
            </p>
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
                <AlertCircle size={15} />{error}
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button 
                type="button" 
                onClick={closeModal} 
                className="px-6 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleDeleteConfirmed}
                disabled={deleting}
                className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors flex items-center gap-2"
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Deleting…" : "Yes, Delete User"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {modal === "reset-password" && resetUser && (
        <Modal title="Reset Password" onClose={closeModal}>
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Key size={24} className="text-emerald-600" />
            </div>
            
            {!tempPassword ? (
              <>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Reset password for "{resetUser.username || resetUser.email}"?
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  A temporary password will be generated. The user will be required to change it on their next login.
                </p>
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
                    <AlertCircle size={15} />{error}
                  </div>
                )}
                <div className="flex gap-3 justify-center">
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-6 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetting}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors flex items-center gap-2"
                    data-testid="confirm-reset-btn"
                  >
                    {resetting ? "Resetting…" : "Reset Password"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Password Reset Successful
                </h3>
                <p className="text-slate-500 text-sm mb-4">
                  Temporary password for <span className="font-semibold text-slate-700">{resetUser.username || resetUser.email}</span>:
                </p>
                
                <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <code className="font-mono text-lg font-semibold text-slate-800 select-all">
                      {tempPassword}
                    </code>
                    <button
                      onClick={copyPassword}
                      className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Copy password"
                    >
                      {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-left">
                  <p className="text-amber-800 text-xs flex items-start gap-2">
                    <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Security Notice:</strong> This password is shown only once. 
                      Communicate it securely to the user (phone, in-person, or secure messaging). 
                      Never send passwords via email.
                    </span>
                  </p>
                </div>
                
                <button 
                  type="button" 
                  onClick={closeModal} 
                  className="w-full px-6 py-2.5 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Reset MFA confirmation modal — admin escape hatch when a
          user has lost their TOTP device and burned through recovery
          codes. Endpoint clears all MFA state + lockout. Idempotent. */}
      {modal === "reset-mfa" && mfaResetUser && (
        <Modal title="Reset Two-Factor Auth" onClose={closeModal}>
          {error && (
            <div data-testid="reset-mfa-error" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
              <AlertCircle size={15} /> {error}
            </div>
          )}
          {mfaResetResult === "ok" ? (
            <>
              <div data-testid="reset-mfa-success" className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg px-4 py-3 mb-4">
                <Check size={16} className="mt-0.5 flex-shrink-0" />
                <span><strong>{mfaResetUser.username}</strong>&apos;s MFA has been cleared. They&apos;ll log in with their password and can re-enroll.</span>
              </div>
              <div className="flex justify-end">
                <button onClick={closeModal} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Done</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-700 mb-2">
                Reset MFA for <strong>{mfaResetUser.username}</strong>?
              </p>
              <p className="text-xs text-slate-500 mb-5">
                This removes the user&apos;s two-factor authentication. They&apos;ll log in
                with their password and can re-enroll. Their lockout (if any) is also
                cleared. This action is audited.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button
                  type="button"
                  data-testid="confirm-reset-mfa"
                  onClick={handleResetMfa}
                  disabled={mfaResetting}
                  className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-60"
                >
                  {mfaResetting ? "Resetting…" : "Reset MFA"}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

    </div>
  );
}

function FormFields({ form, setForm, isCreate, outside = false }) {
  // Location options are fetched lazily (only when a location-tethered role
  // is in play). CLINIC → network-clinic slugs from /scheduling/locations.
  // WS Location (role='location', 2026-09) → WestStar sites from
  // /crm/locations, keyed by their RAINTREE code (the vocabulary
  // crm_patients.assigned_location_code speaks).
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const [locRole, setLocRole] = useState(null);
  const needsLocations = LOCATION_SET_ROLES.includes(form.role);

  useEffect(() => {
    if (!needsLocations || locRole === form.role) return;
    setLocLoading(true);
    setLocRole(form.role);
    const req = form.role === "clinic"
      ? api.get("/scheduling/locations").then((r) => r.data?.locations || [])
      : api.get("/crm/locations").then((r) => (r.data?.locations || [])
          .filter((l) => !l.is_network && l.raintree_code)
          .map((l) => ({ name: l.clinic_name || l.name, city: l.city, location_code: l.raintree_code })));
    req
      .then((rows) => setLocations(rows))
      .catch(() => setLocations([]))
      .finally(() => setLocLoading(false));
  }, [form.role, needsLocations, locRole]);

  const filteredLocs = locQuery
    ? locations.filter((l) => {
        const q = locQuery.toLowerCase();
        return (l.name || "").toLowerCase().includes(q) || (l.city || "").toLowerCase().includes(q) || (l.location_code || "").toLowerCase().includes(q);
      })
    : locations;

  return (
    <>
      {isCreate && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Username</label>
          <input
            data-testid="user-username-input"
            required
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
        <input
          data-testid="user-email-input"
          type="email" required
          disabled={outside}
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">{isCreate ? "Password" : "New Password (leave blank to keep)"}</label>
        <input
          data-testid="user-password-input"
          type="password" required={isCreate}
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          placeholder={isCreate ? "" : "Leave blank to keep current"}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Role</label>
        <select
          data-testid="user-role-select"
          value={form.role}
          disabled={outside}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white"
        >
          {(outside ? [form.role] : (isCreate ? CREATE_ROLES : ROLES)).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
        {outside && (
          <p className="mt-1 text-xs text-slate-500" data-testid="outside-account-note">
            Outside account: role and email can't be changed here. A new password forces a change at next sign-in.
          </p>
        )}
      </div>
      {isCreate && form.role === "partner" && (
        <div data-testid="partner-tenant-section">
          <label className="block text-xs font-medium text-slate-700 mb-1">Partner company tenant id <span className="text-rose-500">*</span></label>
          <input
            data-testid="partner-tenant-input"
            required
            value={form.tenant_id || ""}
            onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
            className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="The company's portal_tenants id"
          />
          <p className="mt-1 text-xs text-slate-500">One named login per coordinator. They must set their own password at first sign-in.</p>
        </div>
      )}
      {/* CLINIC-only: location picker, searchable, native <select> backed
          for keyboard nav. Multi-location logins (2026-08, Green PT pilot):
          selecting adds to the set; chips below remove. At least one is
          required by the backend before a CLINIC user can be created. */}
      {needsLocations && (
        <div data-testid="clinic-location-section">
          <label className="block text-xs font-medium text-slate-700 mb-1">
            {form.role === "clinic" ? "Clinic Locations" : "WS Locations"}{" "}
            {form.role === "clinic" && <span className="text-rose-500">*</span>}
          </label>
          {form.location_codes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="clinic-location-chips">
              {form.location_codes.map((code) => (
                <span key={code} data-testid={`clinic-location-chip-${code}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-xs font-mono text-blue-800">
                  {code}
                  <button type="button" data-testid={`clinic-location-remove-${code}`} className="text-blue-400 hover:text-rose-600 font-bold" onClick={() => setForm((f) => ({ ...f, location_codes: f.location_codes.filter((c) => c !== code) }))}>×</button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            data-testid="clinic-location-search"
            placeholder="Type to filter… name, city, or code"
            value={locQuery}
            onChange={(e) => setLocQuery(e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 mb-2"
          />
          <select
            data-testid="clinic-location-select"
            size={Math.min(6, Math.max(2, filteredLocs.length))}
            value=""
            onChange={(e) => {
              const code = e.target.value;
              if (code) setForm((f) => ({ ...f, location_codes: f.location_codes.includes(code) ? f.location_codes : [...f.location_codes, code] }));
            }}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white"
          >
            {locLoading && <option value="">Loading…</option>}
            {!locLoading && filteredLocs.length === 0 && (
              <option value="">
                {locations.length === 0 ? "Location roster unavailable" : "No matches"}
              </option>
            )}
            {!locLoading && filteredLocs.map((l) => (
              <option key={l.location_code} value={l.location_code}>
                {l.name}{l.city ? ` — ${l.city}` : ""}
              </option>
            ))}
          </select>
          <div className="text-[11px] text-slate-500 mt-1">
            {locations.length > 0 ? `${filteredLocs.length} of ${locations.length} locations` : ""}
            {!locLoading && locations.length === 0 && (
              <span className="text-rose-600" data-testid="location-roster-unavailable">
                Location roster unavailable — refresh the locations cache, then reopen this form.
              </span>
            )}
            {form.location_codes.length > 0 && <span className="ml-2 text-slate-700">{form.location_codes.length} selected</span>}
            {form.role === "location" && form.location_codes.length === 0 && (
              <span className="ml-2 text-amber-700">No locations assigned — this user will see an empty queue.</span>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          data-testid="user-active-checkbox"
          type="checkbox" id="is_active"
          checked={form.is_active}
          onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
        />
        <label htmlFor="is_active" className="text-sm text-slate-700">Active account</label>
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid="user-force-reset-checkbox"
          type="checkbox" id="force_password_change"
          checked={form.force_password_change}
          onChange={(e) => setForm((f) => ({ ...f, force_password_change: e.target.checked }))}
          className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-600"
        />
        <label htmlFor="force_password_change" className="text-sm text-slate-700 flex items-center gap-1.5">
          <ShieldAlert size={13} className="text-amber-600" />
          Force password change on first login
        </label>
      </div>
    </>
  );
}
